/**
 * The **shape** of a policy document, as the wire carries it.
 *
 * These interfaces are structural only. They say that a selector has `any` and
 * `all` arrays of clauses; they do not say what an operator may be, which fields
 * are required, or which of them the pinned build actually reads. Every one of
 * those questions is answered at runtime from the form schema (`schema.ts`),
 * because that is the artifact a drift check defends (`I3`, `SC-F501`).
 *
 * The split is deliberate. An editor needs *somewhere* to put the object it is
 * holding, and `Record<string, unknown>` with string-keyed lookups everywhere
 * would be a form that cannot be type-checked at all. What must never live here
 * is a vocabulary: no `type Effect = "allow" | "deny"`, no operator union, no
 * enforcement-mode literal. Those arrive as strings from the API and are rendered
 * from the schema's own enum entries.
 *
 * One wire subtlety this module owns: **a typed scalar marshals as a bare JSON
 * scalar.** `policymodel.TypedValue` emits the value and not the tag, and recovers
 * the tag from the JSON shape on the way back in, because Sluice's field is an
 * `any` compared type-strictly — a tagged object there would compare against
 * nothing. So the authored type is a property of the *form*, not of the document,
 * and [inferValueType] is the client mirror of that recovery.
 */

/** ScalarValue is a `policymodel.TypedValue` as it appears in JSON: bare. */
export type ScalarValue = string | number | boolean | null;

/** ValueTypeName is the authored type of a scalar. The vocabulary is the
 * schema's `ValueType` enum; the alias exists so the code reads, not to fix the
 * list — an unknown value from a newer schema still renders. */
export type ValueTypeName = string;

/** ClaimCheck is one JWT claim comparison. */
export interface ClaimCheck {
  claim: string;
  op: string;
  value?: ScalarValue;
  values?: ScalarValue[];
  pattern?: string;
}

/** SubjectSelector is the subject half of a clause. Every populated field ANDs
 * with the others. */
export interface SubjectSelector {
  jwtClaims?: ClaimCheck[];
  groups?: string[];
  apiKeys?: string[];
  ipRanges?: string[];
  roles?: string[];
}

/** ResourceSelector is the resource half of a clause. */
export interface ResourceSelector {
  catalogs?: string[];
  schemas?: string[];
  tables?: string[];
  columns?: string[];
  tags?: string[];
  actions?: string[];
}

/** Clause is an optional subject half plus an optional resource half, ANDed.
 * Both omissions are the most common authoring mistake in the product: a clause
 * with only `resources` matches every subject including anonymous, and one with
 * only `subjects` matches every table. */
export interface Clause {
  subjects?: SubjectSelector;
  resources?: ResourceSelector;
}

/** Selector is the shared matcher. `any` and `all` combine as **OR**. */
export interface Selector {
  any?: Clause[];
  all?: Clause[];
}

/** Condition is a named CEL expression. */
export interface Condition {
  name: string;
  expression: string;
}

/**
 * Predicate is one node of a row-filter tree: a conjunction, a disjunction, a
 * negation, or a leaf comparison. **Exactly one** of the four shapes may be
 * populated — upstream a node carrying both an internal node and a leaf silently
 * discards the leaf — so the editor writes one shape per node.
 */
export interface Predicate {
  all?: Predicate[];
  any?: Predicate[];
  not?: Predicate;
  column?: string;
  op?: string;
  value?: ScalarValue;
  values?: ScalarValue[];
}

/** FilterSpec is the filter in exactly one of its two forms. Populating both, or
 * neither, is a decode error. */
export interface FilterSpec {
  predicate?: Predicate;
  expression?: string;
}

/** SqlAccessSpec is `SqlAccessPolicy.spec`. */
export interface SqlAccessSpec {
  enforcementMode?: string;
  effect?: string;
  match: Selector;
  exclude?: Selector;
  conditions?: Condition[];
  message?: string;
  errorCode?: string;
}

/** RowFilterSpec is `RowFilterPolicy.spec`. */
export interface RowFilterSpec {
  enforcementMode?: string;
  match: Selector;
  exclude?: Selector;
  conditions?: Condition[];
  filter: FilterSpec;
  combine?: string;
}

/** PredicateShape names which of a node's four shapes is populated. */
export type PredicateShape = "all" | "any" | "not" | "leaf";

/** shapeOf reports a node's shape, in the order Sluice's own compiler branches:
 * `all`, then `any`, then `not`, then the leaf. A node that (illegally) populates
 * more than one therefore reads here exactly as the engine would read it. */
export function shapeOf(node: Predicate): PredicateShape {
  if (node.all) return "all";
  if (node.any) return "any";
  if (node.not) return "not";
  return "leaf";
}

/** emptyPredicate is a fresh leaf. `op` is left blank so the operator select
 * starts unchosen: `PredOp` is validated at compile, so a defaulted operator
 * would be a value the author did not pick surviving into the publish. */
export function emptyPredicate(): Predicate {
  return { column: "", op: "" };
}

/** emptyClause is a fresh, entirely unconstrained clause. It is deliberately
 * empty rather than pre-seeded: a clause that arrives already matching every
 * subject and every table would be a default nobody chose. */
export function emptyClause(): Clause {
  return {};
}

/** isTemplate mirrors `policymodel.IsTemplate`: Sluice compiles any string
 * containing both `{{` and `}}` as a template, wherever they sit. That is also
 * why `"t-{{ x }}"` is a compile error rather than a literal — the string is
 * recognised as a template and then refused for not spanning the whole value. */
export function isTemplate(s: string): boolean {
  return s.includes("{{") && s.includes("}}");
}

/** isWholeStringTemplate reports whether a template spans the entire value,
 * which is the only form Sluice compiles (`L13`). */
export function isWholeStringTemplate(s: string): boolean {
  const t = s.trim();
  if (!t.startsWith("{{") || !t.endsWith("}}")) return false;
  const inner = t.slice(2, -2).trim();
  if (inner === "") return false;
  return inner.split(".").every((seg) => seg.trim() !== "");
}

/**
 * inferValueType recovers the authored type from a bare JSON scalar, the way
 * `policymodel.TypedValue.UnmarshalJSON` does.
 *
 * One divergence is unavoidable and is the same one the Go side documents: Go
 * reads numbers through `json.Number` and keeps `1` and `1.0` distinguishable,
 * while `JSON.parse` has already collapsed them. An integral float therefore
 * re-reads as `int` here — which is exactly what the renderer's
 * shortest-round-trip rule produces anyway, and Sluice coerces numerically across
 * int and float, so the meaning is unchanged.
 */
export function inferValueType(v: ScalarValue | undefined): ValueTypeName {
  if (v === null) return "null";
  if (v === undefined) return "string";
  if (typeof v === "boolean") return "bool";
  if (typeof v === "number") return Number.isInteger(v) ? "int" : "float";
  return isTemplate(v) ? "template" : "string";
}

/** coerceValue converts the text in an input to the scalar a given authored type
 * puts on the wire. An unparseable number stays a string, so the field does not
 * silently discard what the author typed while they are still typing it. */
export function coerceValue(type: ValueTypeName, text: string): ScalarValue {
  switch (type) {
    case "null":
      return null;
    case "bool":
      return text === "true";
    case "int": {
      const n = Number.parseInt(text, 10);
      return Number.isNaN(n) ? text : n;
    }
    case "float": {
      const n = Number.parseFloat(text);
      return Number.isNaN(n) ? text : n;
    }
    default:
      return text;
  }
}

/** valueText renders a scalar back into an input's text. */
export function valueText(v: ScalarValue | undefined): string {
  if (v === undefined || v === null) return "";
  return String(v);
}

/** clone deep-copies a plain JSON document. The editors hold their spec as
 * immutable-by-convention state and re-render on replacement, so every mutation
 * goes through a copy — `structuredClone` rather than a JSON round trip because it
 * preserves `undefined`-free objects without re-parsing. */
export function clone<T>(value: T): T {
  return structuredClone(value);
}

/** asRecord narrows an unknown spec into an indexable object without asserting
 * anything about its contents, which is what a form loading a document authored
 * by another build needs. */
export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
